import React, { useCallback } from "react";
import { View } from "react-native";
import { useTranslation } from "locales";
import {
    Button,
    DataTable,
    InputControl,
    useThemedStyle,
    CheckBox,
    Text,
    TextInput,
    RequiredText,
    IconButton,
} from "@venuepos/react-common";
import type { Form, StyleFunction } from "@venuepos/react-common";
import { IMiscButtonsInput, defaultMiscButtons } from "lib";
import type { MiscButtonsForm } from "./forms";
import { produce } from "immer";

export function MiscButtonForm(props: {
    form: Form<IMiscButtonsInput>;
    onSubmit: () => void;
    submitButton: [string, string];
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ values, errors }, { setValue, handleSubmit }] = props.form;

    const onMoveUp = useCallback(
        (buttonKey: string) => {
            if (!values) {
                return;
            }

            setValue(
                "buttonsSortOrder",
                produce(values.buttonsSortOrder, draft => {
                    const button = draft.find(itr => itr.label === buttonKey);
                    const prevButton = draft.find(
                        itr => itr.showOrder === button.showOrder - 1
                    );
                    // Make sure that we have the buttons
                    if (!button || !prevButton) {
                        console.error(
                            "onMoveUp could not find button or prevButton"
                        );
                        return;
                    }

                    button.showOrder -= 1;
                    if (button.showOrder < 0) {
                        console.error(
                            "button.showOrder is negative",
                            button.showOrder
                        );
                    }
                    prevButton.showOrder += 1;
                })
            );
        },
        [setValue, values]
    );

    const onMoveDown = useCallback(
        (buttonKey: string) => {
            if (!values) {
                return;
            }

            setValue(
                "buttonsSortOrder",
                produce(values.buttonsSortOrder, draft => {
                    const button = draft.find(itr => itr.label === buttonKey);
                    const nextButton = draft.find(
                        itr => itr.showOrder === button.showOrder + 1
                    );
                    // Make sure that we have the buttons
                    if (!button || !nextButton) {
                        console.error(
                            "onMoveUp could not find button or nextButton"
                        );
                        return;
                    }

                    button.showOrder += 1;
                    nextButton.showOrder -= 1;
                    if (button.showOrder < 0) {
                        console.error(
                            "button.nextButton is negative",
                            button.showOrder
                        );
                    }
                })
            );
        },
        [setValue, values]
    );

    if (!values) {
        return null;
    }

    return (
        <>
            <View style={styles.nameContainer}>
                <InputControl error={errors.name}>
                    <TextInput
                        label={t("common.name", "Name")}
                        placeholder={t(
                            "backoffice.common.enter_name",
                            "Enter name"
                        )}
                        defaultValue={values.name}
                        onChangeText={text => setValue("name", text)}
                        required={true}
                    />
                </InputControl>
            </View>
            <DataTable>
                <DataTable.Header>
                    <DataTable.Title>
                        {t(
                            "backoffice.misc_buttons_form_add_function_key",
                            "Show"
                        )}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t("common.name", "Name")}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t(
                            "backoffice.misc_buttons_form.description",
                            "Description"
                        )}
                    </DataTable.Title>
                </DataTable.Header>

                {[...values.buttonsSortOrder]
                    .sort((a, b) => a.showOrder - b.showOrder)
                    .map((buttonKey, index) => (
                        <DataTable.Row key={buttonKey.label}>
                            <DataTable.Cell style={styles.checkboxCell}>
                                <CheckBox
                                    value={
                                        values.buttons[
                                            buttonKey.label as keyof MiscButtonsForm["buttons"]
                                        ]
                                    }
                                    onValueChange={value => {
                                        setValue(
                                            "buttons",
                                            produce(values.buttons, draft => {
                                                draft[
                                                    buttonKey.label as keyof MiscButtonsForm["buttons"]
                                                ] = value;
                                            })
                                        );
                                    }}
                                />
                            </DataTable.Cell>
                            <DataTable.Cell>
                                <Text>
                                    {t(`misc_buttons.${buttonKey.label}`)}
                                </Text>
                            </DataTable.Cell>
                            <DataTable.Cell>
                                <Text key={buttonKey.label}>
                                    {t(
                                        `misc_buttons.descriptions.${buttonKey.label}`,
                                        `misc_buttons.descriptions.${buttonKey.label}`
                                    )}
                                </Text>
                            </DataTable.Cell>
                            <DataTable.Cell style={styles.iconColumn}>
                                {index > 0 ? (
                                    <IconButton
                                        color={styles.iconColor.color}
                                        name="angle-up"
                                        onPress={() =>
                                            onMoveUp(
                                                buttonKey.label as keyof MiscButtonsForm["buttons"]
                                            )
                                        }
                                        testID="moveUp"
                                    />
                                ) : null}
                            </DataTable.Cell>
                            <DataTable.Cell style={styles.iconColumn}>
                                {index + 1 <
                                Object.keys(defaultMiscButtons).length ? (
                                    <IconButton
                                        color={styles.iconColor.color}
                                        name="angle-down"
                                        onPress={() =>
                                            onMoveDown(
                                                buttonKey.label as keyof MiscButtonsForm["buttons"]
                                            )
                                        }
                                        testID="moveDown"
                                    />
                                ) : null}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ))}
            </DataTable>

            <Button
                style={styles.saveButton}
                onPress={handleSubmit(props.onSubmit)}
            >
                {t(props.submitButton[0], props.submitButton[1])}
            </Button>

            <RequiredText />
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    iconColor: {
        color: theme.colors.textDark,
    },
    iconColumn: {
        justifyContent: "center",
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 40,
    },

    nameContainer: {
        paddingLeft: theme.spacingScale * 4,
        maxWidth: theme.dimensions.maxFormWidth,
    },
    saveButton: {
        marginTop: theme.spacingScale * 2,
    },
    checkboxCell: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 180,
        paddingLeft: 50,
    },
});
